import React, { useState, useEffect } from 'react';
import {
  User,
  Bell,
  Shield,
  Moon,
  Sun,
  Save,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building,
  CreditCard,
  Users,
  Key,
  Lock,
  Settings,
  FileText,
  Eye,
  EyeOff,
  Download,
  ExternalLink,
  CheckCircle,
  Clock,
  File,
  Trash2,
  AlertTriangle,
  Loader2,
  Upload,
  Camera
} from 'lucide-react';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import WorkdayClientEmailChangeModal from '../../components/workday-client/WorkdayClientEmailChangeModal';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';
import { getBillingState, BillingSubscription } from '../../lib/billingState';

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  amount: number;
  description: string;
  invoice_url: string | null;
  is_paid: boolean;
  paid_date: string | null;
}

interface Credentials {
  id: string;
  username: string;
  password: string;
  login_url: string;
  access_granted_date: string;
  notes: string;
}

interface Resource {
  id: string;
  title: string;
  description: string;
  resource_url: string;
  resource_type: string;
  is_global: boolean;
}

export default function WorkdayClientSettings() {
  const { theme, toggleTheme } = useWorkdayClientTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('profile');
  const [settings, setSettings] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    title: '',
    company: '',
    companyWebsite: '',
    industry: '',
    companySize: '',
    numberOfEmployees: '',
    hiringNeeds: '',
    budgetRange: '',
    timeline: '',
    projectRequirements: '',
    issuesAndModules: '',
    implementationStages: '',
    implementationPartner: '',
    goLiveDate: '',
    enhancements: '',

    emailNotifications: true,
    smsNotifications: false,
    projectUpdates: true,
    consultantApplications: true,
    timesheetNotifications: true,
    billingAlerts: true,

    profileVisibility: 'company',
    showContactInfo: true,
    allowDirectMessages: true,

    workdayClientRole: 'account_owner',
    billingAccess: true,
    canRequestAdmin: true
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [existingDeletionRequest, setExistingDeletionRequest] = useState<{ status: string; requested_at: string } | null>(null);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [credentialsLoading, setCredentialsLoading] = useState(true);
  const [resourcesLoading, setResourcesLoading] = useState(true);

  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoStoragePath, setPhotoStoragePath] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const [billingSub, setBillingSub] = useState<BillingSubscription | null>(null);

  useEffect(() => {
    fetchUserSettings();
    fetchInvoices();
    fetchCredentials();
    fetchResources();
    fetchExistingDeletionRequest();
    fetchBillingSub();
  }, [user]);

  const fetchBillingSub = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('billing_subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    setBillingSub(data ?? null);
  };

  const fetchUserSettings = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data: profileData, error: profileError } = await supabase
        .from('workday_client_profiles')
        .select(`
          *,
          companies:company_id (
            name,
            industry,
            size,
            website
          )
        `)
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        setLoading(false);
        return;
      }

      const { data: serviceData } = await supabase
        .from('workday_client_service_requirements')
        .select('*')
        .eq('customer_id', user.id)
        .maybeSingle();

      if (profileData) {
        if (profileData.photo_url) {
          setPhotoStoragePath(profileData.photo_url);
          const { data: urlData } = supabase.storage
            .from('profile-photos')
            .getPublicUrl(profileData.photo_url);
          setPhotoUrl(urlData.publicUrl);
        }

        setSettings({
          firstName: profileData.first_name || '',
          lastName: profileData.last_name || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          location: profileData.country_region || '',
          title: profileData.title || '',
          company: profileData.companies?.name || '',
          companyWebsite: profileData.companies?.website || '',
          industry: profileData.companies?.industry || '',
          companySize: profileData.company_size || '',
          hiringNeeds: profileData.hiring_needs || '',
          budgetRange: profileData.budget_range || '',
          timeline: profileData.timeline || '',
          projectRequirements: profileData.project_requirements || '',
          numberOfEmployees: serviceData?.number_of_employees || '',
          issuesAndModules: serviceData?.issues_and_modules || '',
          implementationStages: serviceData?.implementation_stages || '',
          implementationPartner: serviceData?.implementation_partner || '',
          goLiveDate: serviceData?.go_live_date || '',
          enhancements: serviceData?.enhancements || '',

          emailNotifications: true,
          smsNotifications: false,
          projectUpdates: true,
          consultantApplications: true,
          timesheetNotifications: true,
          billingAlerts: true,

          profileVisibility: 'company',
          showContactInfo: true,
          allowDirectMessages: true,

          workdayClientRole: profileData.role || 'team_member',
          billingAccess: profileData.billing_access || false,
          canRequestAdmin: true
        });
      }

      setLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage('Please upload a JPG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage('Image size must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    setMessage('');
    try {
      const ext = file.name.split('.').pop();
      const filePath = `${user.id}/profile-photo.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase
        .from('workday_client_profiles')
        .update({ photo_url: filePath })
        .eq('id', user.id);
      if (updateError) throw updateError;

      const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(filePath);
      setPhotoStoragePath(filePath);
      setPhotoUrl(urlData.publicUrl);
      setMessage('Profile photo updated successfully!');
    } catch (err: any) {
      setMessage(err.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handlePhotoDelete = async () => {
    if (!user?.id || !photoStoragePath) return;
    setDeletingPhoto(true);
    try {
      await supabase.storage.from('profile-photos').remove([photoStoragePath]);
      await supabase.from('workday_client_profiles').update({ photo_url: null }).eq('id', user.id);
      setPhotoUrl(null);
      setPhotoStoragePath(null);
      setMessage('Profile photo removed');
    } catch (err: any) {
      setMessage(err.message || 'Failed to remove photo');
    } finally {
      setDeletingPhoto(false);
    }
  };

  const fetchInvoices = async () => {
    if (!user) {
      setInvoicesLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('workday_client_invoices')
        .select('*')
        .eq('customer_profile_id', user.id)
        .order('invoice_date', { ascending: false });

      if (error) {
        console.error('Error fetching invoices:', error);
      } else {
        setInvoices(data || []);
      }

      setInvoicesLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setInvoicesLoading(false);
    }
  };

  const fetchCredentials = async () => {
    if (!user) {
      setCredentialsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('workday_client_credentials')
        .select('*')
        .eq('customer_profile_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching credentials:', error);
      } else {
        setCredentials(data);
      }

      setCredentialsLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setCredentialsLoading(false);
    }
  };

  const fetchResources = async () => {
    if (!user) {
      setResourcesLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('workday_client_resources')
        .select('*')
        .or(`customer_profile_id.eq.${user.id},is_global.eq.true`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching resources:', error);
      } else {
        setResources(data || []);
      }

      setResourcesLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setResourcesLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    setMessage('');

    try {
      const { data: profileData } = await supabase
        .from('workday_client_profiles')
        .select('company_id')
        .eq('id', user.id)
        .maybeSingle();

      if (profileData?.company_id && settings.company) {
        const { error: companyError } = await supabase
          .from('companies')
          .update({
            name: settings.company,
            industry: settings.industry,
            website: settings.companyWebsite,
            size: settings.companySize,
            updated_at: new Date().toISOString()
          })
          .eq('id', profileData.company_id);

        if (companyError) {
          console.error('Error updating company:', companyError);
          setMessage('Error saving company information. Please contact support.');
          setSaving(false);
          return;
        }
      }

      const { error: profileError } = await supabase
        .from('workday_client_profiles')
        .update({
          first_name: settings.firstName,
          last_name: settings.lastName,
          phone: settings.phone,
          country_region: settings.location,
          title: settings.title,
          company_size: settings.companySize,
          hiring_needs: settings.hiringNeeds,
          budget_range: settings.budgetRange,
          timeline: settings.timeline,
          project_requirements: settings.projectRequirements,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (profileError) {
        setMessage('Error saving settings. Please try again.');
        console.error('Error updating profile:', profileError);
      } else {
        const { data: existingService } = await supabase
          .from('workday_client_service_requirements')
          .select('id')
          .eq('customer_id', user.id)
          .maybeSingle();

        if (existingService) {
          await supabase
            .from('workday_client_service_requirements')
            .update({
              number_of_employees: settings.numberOfEmployees,
              issues_and_modules: settings.issuesAndModules,
              implementation_stages: settings.implementationStages,
              implementation_partner: settings.implementationPartner,
              go_live_date: settings.goLiveDate || null,
              enhancements: settings.enhancements,
              project_requirements: settings.projectRequirements,
              updated_at: new Date().toISOString()
            })
            .eq('customer_id', user.id);
        } else if (settings.numberOfEmployees || settings.issuesAndModules || settings.implementationStages || settings.implementationPartner || settings.enhancements) {
          await supabase
            .from('workday_client_service_requirements')
            .insert({
              customer_id: user.id,
              number_of_employees: settings.numberOfEmployees,
              issues_and_modules: settings.issuesAndModules,
              implementation_stages: settings.implementationStages,
              implementation_partner: settings.implementationPartner,
              go_live_date: settings.goLiveDate || null,
              enhancements: settings.enhancements,
              project_requirements: settings.projectRequirements
            });
        }

        setMessage('Settings saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      setMessage('Error saving settings. Please try again.');
      console.error('Error:', err);
    }

    setSaving(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getDaysRemaining = () => {
    if (!credentials?.access_granted_date) return null;

    const grantedDate = new Date(credentials.access_granted_date);
    const trialEnd = new Date(grantedDate);
    trialEnd.setDate(trialEnd.getDate() + 14);

    const today = new Date();
    const diffTime = trialEnd.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0;
  };

  const fetchExistingDeletionRequest = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('account_deletion_requests')
      .select('status, requested_at')
      .eq('user_id', user.id)
      .eq('user_type', 'workday_client')
      .maybeSingle();
    if (data) setExistingDeletionRequest(data);
  };

  const handleDeleteAccountRequest = async () => {
    if (!user?.id) return;
    setIsSubmittingDelete(true);
    setDeleteMessage('');
    try {
      const { error } = await supabase
        .from('account_deletion_requests')
        .insert({
          user_id: user.id,
          user_type: 'workday_client',
          reason: deleteReason.trim() || null,
          status: 'pending'
        });
      if (error) throw error;
      setDeleteMessage('Your account deletion request has been submitted. An administrator will review it shortly.');
      setShowDeleteConfirm(false);
      setDeleteReason('');
      setExistingDeletionRequest({ status: 'pending', requested_at: new Date().toISOString() });
    } catch (err: any) {
      setDeleteMessage(err.message?.includes('duplicate') || err.code === '23505'
        ? 'You already have a pending deletion request.'
        : 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmittingDelete(false);
    }
  };

  const handleEmailChangeSuccess = (newEmail: string) => {
    setSettings(prev => ({ ...prev, email: newEmail }));
    setShowEmailModal(false);
    setMessage('Email updated successfully!');
    setTimeout(() => setMessage(''), 4000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#40E0D0]"></div>
      </div>
    );
  }

  return (
    <>
    {showEmailModal && (
      <WorkdayClientEmailChangeModal
        currentEmail={settings.email}
        onClose={() => setShowEmailModal(false)}
        onSuccess={handleEmailChangeSuccess}
        theme={theme}
      />
    )}
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark'
        ? 'bg-gray-900 text-white'
        : 'bg-gray-50 text-gray-900'
    }`}>
      <WorkdayClientHeader showBack />

      <div className="max-w-7xl mx-auto px-6 pt-32 pb-12">
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/workday-client/dashboard')}
            className={`mr-4 p-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <Settings className="w-8 h-8 mr-3 text-[#40E0D0]" />
              Account Settings
            </h1>
            <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage your account preferences and information
            </p>
          </div>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.includes('Error')
              ? 'bg-red-100 text-red-800 border border-red-200'
              : 'bg-green-100 text-green-800 border border-green-200'
          }`}>
            {message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className={`lg:col-span-1 ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          } border rounded-xl p-6 h-fit lg:sticky lg:top-32`}>
            <nav className="space-y-2">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'profile'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <User className="w-5 h-5 mr-3" />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'notifications'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Bell className="w-5 h-5 mr-3" />
                Notifications
              </button>
              <button
                onClick={() => setActiveTab('alm-access')}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'alm-access'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Key className="w-5 h-5 mr-3" />
                PerplexityALM Access
              </button>
              <button
                onClick={() => setActiveTab('invoices')}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'invoices'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <CreditCard className="w-5 h-5 mr-3" />
                Invoices & Subscriptions
              </button>
              <button
                onClick={() => navigate('/workday-client/invite-team')}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Users className="w-5 h-5 mr-3" />
                Invite Team-member
              </button>
              <button
                onClick={() => navigate('/workday-client/privacy')}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Shield className="w-5 h-5 mr-3" />
                Privacy
              </button>
              <button
                onClick={() => setActiveTab('2fa-security')}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === '2fa-security'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Shield className="w-5 h-5 mr-3 text-[#40E0D0]" />
                2FA Security
              </button>
              <button
                onClick={toggleTheme}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5 mr-3" /> : <Moon className="w-5 h-5 mr-3" />}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={() => setActiveTab('delete-account')}
                className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'delete-account'
                    ? 'bg-red-100 text-red-700'
                    : 'text-red-500 hover:bg-red-50 hover:text-red-600'
                } ${theme === 'dark' ? 'hover:bg-red-900/20' : ''}`}
              >
                <Trash2 className="w-5 h-5 mr-3" />
                Delete Account
              </button>
            </nav>
          </aside>

          <div className="lg:col-span-3 space-y-8">
            {activeTab === 'profile' && (
              <>
              {/* Profile Photo */}
              <section className={`${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-200'
              } border rounded-xl p-6`}>
                <h2 className="text-xl font-semibold mb-6 flex items-center">
                  <Camera className="w-5 h-5 mr-2 text-[#40E0D0]" />
                  Profile Photo
                </h2>
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                  {/* Avatar circle */}
                  <div className="relative flex-shrink-0">
                    <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-[#40E0D0] bg-gray-200 flex items-center justify-center">
                      {photoUrl ? (
                        <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-12 h-12 text-gray-400" />
                      )}
                    </div>
                    {photoUrl && (
                      <button
                        onClick={handlePhotoDelete}
                        disabled={deletingPhoto}
                        className="absolute -top-1 -right-1 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow transition-colors disabled:opacity-50"
                        title="Remove photo"
                      >
                        {deletingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </div>

                  {/* Upload controls */}
                  <div className="flex flex-col gap-3">
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Upload a round profile photo. JPG, PNG or WebP, max 5MB.
                    </p>
                    <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm cursor-pointer transition-colors ${
                      uploadingPhoto ? 'opacity-50 cursor-not-allowed' : ''
                    } bg-[#40E0D0] hover:bg-[#35c5b5] text-black`}>
                      {uploadingPhoto ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                      ) : (
                        <><Upload className="w-4 h-4" /> {photoUrl ? 'Change Photo' : 'Upload Photo'}</>
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        className="hidden"
                        disabled={uploadingPhoto}
                        onChange={handlePhotoUpload}
                      />
                    </label>
                  </div>
                </div>
              </section>

              {/* Personal Information */}
              <section className={`${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-200'
              } border rounded-xl p-6`}>
                <h2 className="text-xl font-semibold mb-6 flex items-center">
                  <User className="w-5 h-5 mr-2 text-[#40E0D0]" />
                  Personal Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      First Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={settings.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Last Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={settings.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Email
                    </label>
                    <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600'
                        : 'bg-gray-50 border-gray-300'
                    }`}>
                      <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className={`flex-1 text-sm truncate ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                        {settings.email || 'No email set'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowEmailModal(true)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#40E0D0] hover:text-[#40E0D0]/80 hover:bg-[#40E0D0]/10 rounded transition-colors flex-shrink-0"
                      >
                        <Lock className="w-3 h-3" />
                        Change
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Phone
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        value={settings.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Title
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={settings.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Location
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={settings.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>
                </div>

                <h2 className="text-xl font-semibold mb-6 mt-8 flex items-center">
                  <Building className="w-5 h-5 mr-2 text-[#40E0D0]" />
                  Company Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Company Name
                    </label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={settings.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Company Website
                    </label>
                    <div className="relative">
                      <ExternalLink className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="url"
                        value={settings.companyWebsite}
                        onChange={(e) => handleInputChange('companyWebsite', e.target.value)}
                        placeholder="https://example.com"
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Industry
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={settings.industry}
                        onChange={(e) => handleInputChange('industry', e.target.value)}
                        placeholder="e.g., Technology, Healthcare, Finance"
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Company Size
                    </label>
                    <div className="relative">
                      <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        value={settings.companySize}
                        onChange={(e) => handleInputChange('companySize', e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="">Select company size</option>
                        <option value="1-50">1-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-1000">201-1000 employees</option>
                        <option value="1000+">1000+ employees</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Number of Employees
                    </label>
                    <div className="relative">
                      <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        value={settings.numberOfEmployees}
                        onChange={(e) => handleInputChange('numberOfEmployees', e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="">Select range</option>
                        <option value="1-50">1-50</option>
                        <option value="51-200">51-200</option>
                        <option value="201-500">201-500</option>
                        <option value="501-1000">501-1000</option>
                        <option value="1001-5000">1001-5000</option>
                        <option value="5000+">5000+</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Workday Services Needed
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={settings.hiringNeeds}
                        onChange={(e) => handleInputChange('hiringNeeds', e.target.value)}
                        placeholder="HCM, Financials, Planning, etc."
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Budget Range (per hour)
                    </label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={settings.budgetRange}
                        onChange={(e) => handleInputChange('budgetRange', e.target.value)}
                        placeholder="e.g., $75-100/hour"
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Project Timeline
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        value={settings.timeline}
                        onChange={(e) => handleInputChange('timeline', e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="">Select timeline</option>
                        <option value="immediate">Immediate</option>
                        <option value="1-month">Within 1 month</option>
                        <option value="3-months">Within 3 months</option>
                        <option value="ongoing">Ongoing service needs</option>
                      </select>
                    </div>
                  </div>

                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-3 bg-[#40E0D0] text-black font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </section>
              </>
            )}

            {activeTab === 'notifications' && (
              <section className={`${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-200'
              } border rounded-xl p-6`}>
                <h2 className="text-xl font-semibold mb-6 flex items-center">
                  <Bell className="w-5 h-5 mr-2 text-[#40E0D0]" />
                  Notification Preferences
                </h2>

                <div className="space-y-4">
                  {[
                    { key: 'emailNotifications', label: 'Email Notifications', description: 'Receive email notifications for important updates' },
                    { key: 'smsNotifications', label: 'SMS Notifications', description: 'Receive text message notifications' },
                    { key: 'projectUpdates', label: 'Project Updates', description: 'Get notified about project milestones and changes' },
                    { key: 'consultantApplications', label: 'Consultant Applications', description: 'Notifications for new consultant applications' },
                    { key: 'timesheetNotifications', label: 'Timesheet Notifications', description: 'Alerts for timesheet submissions and approvals' },
                    { key: 'billingAlerts', label: 'Billing Alerts', description: 'Receive notifications about billing and payment updates' }
                  ].map(({ key, label, description }) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{label}</h3>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {description}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings[key as keyof typeof settings] as boolean}
                          onChange={(e) => handleInputChange(key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#40E0D0]/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#40E0D0]"></div>
                      </label>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-3 bg-[#40E0D0] text-black font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </section>
            )}

            {activeTab === 'alm-access' && (
              <div className="space-y-6">
                <section className={`${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700'
                    : 'bg-white border-gray-200'
                } border rounded-xl p-6`}>
                  <h2 className="text-xl font-semibold mb-6 flex items-center">
                    <Key className="w-5 h-5 mr-2 text-[#40E0D0]" />
                    PerplexityALM Access
                  </h2>

                  {credentialsLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div>
                    </div>
                  ) : credentials ? (
                    <div className="space-y-6">
                      <div className={`p-4 rounded-lg border ${
                        theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg">Access Status</h3>
                          <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Active
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              Access Granted
                            </p>
                            <p className="font-medium">
                              {formatDate(credentials.access_granted_date)}
                            </p>
                          </div>
                          <div>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              Trial Status
                            </p>
                            <p className="font-medium flex items-center">
                              <Clock className="w-4 h-4 mr-2 text-[#40E0D0]" />
                              {getDaysRemaining()} days remaining
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className={`p-6 rounded-lg border-2 ${
                        theme === 'dark'
                          ? 'bg-gradient-to-br from-gray-800 to-gray-700 border-[#40E0D0]'
                          : 'bg-gradient-to-br from-white to-gray-50 border-[#40E0D0]'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg">Login Credentials</h3>
                          <button
                            onClick={() => window.open(credentials.login_url, '_blank')}
                            className="flex items-center px-4 py-2 bg-[#40E0D0] text-black font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Go to PerplexityALM
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Username
                            </label>
                            <div className={`p-3 rounded-lg font-mono ${
                              theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                            }`}>
                              {credentials.username}
                            </div>
                          </div>

                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Password
                            </label>
                            <div className="relative">
                              <div className={`p-3 rounded-lg font-mono pr-12 ${
                                theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                              }`}>
                                {showPassword ? credentials.password : '••••••••••••'}
                              </div>
                              <button
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#40E0D0] hover:text-[#40E0D0]/80"
                              >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>

                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Login URL
                            </label>
                            <a
                              href={credentials.login_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`block p-3 rounded-lg font-mono text-[#40E0D0] hover:underline ${
                                theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                              }`}
                            >
                              {credentials.login_url}
                            </a>
                          </div>

                          {credentials.notes && (
                            <div>
                              <label className={`block text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                Additional Notes
                              </label>
                              <div className={`p-3 rounded-lg ${
                                theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                              }`}>
                                {credentials.notes}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={`text-center py-12 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      <Key className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No Access Credentials Yet</p>
                      <p>Your PerplexityALM access credentials will appear here once they are set up by an administrator.</p>
                    </div>
                  )}
                </section>

                <section className={`${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700'
                    : 'bg-white border-gray-200'
                } border rounded-xl p-6`}>
                  <h2 className="text-xl font-semibold mb-6 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-[#40E0D0]" />
                    Resource Documents
                  </h2>

                  {resourcesLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div>
                    </div>
                  ) : resources.length > 0 ? (
                    <div className="space-y-3">
                      {resources.map((resource) => (
                        <div
                          key={resource.id}
                          className={`p-4 rounded-lg border transition-colors hover:border-[#40E0D0] ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-2">
                                <File className="w-5 h-5 mr-2 text-[#40E0D0]" />
                                <h3 className="font-semibold">{resource.title}</h3>
                                {resource.is_global && (
                                  <span className="ml-2 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                    Global
                                  </span>
                                )}
                              </div>
                              {resource.description && (
                                <p className={`text-sm mb-2 ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  {resource.description}
                                </p>
                              )}
                              <p className={`text-xs ${
                                theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                              }`}>
                                Type: {resource.resource_type}
                              </p>
                            </div>
                            <a
                              href={resource.resource_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-4 flex items-center px-4 py-2 bg-[#40E0D0] text-black font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              View
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-center py-12 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No Resources Available</p>
                      <p>Resource documents will appear here once they are uploaded by an administrator.</p>
                    </div>
                  )}
                </section>
              </div>
            )}

            {activeTab === 'invoices' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold flex items-center">
                    <CreditCard className="w-5 h-5 mr-2 text-[#40E0D0]" />
                    Invoices &amp; Subscriptions
                  </h2>
                </div>

                {/* Plan cards side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

                {/* Current Plan Card */}
                <div className={`rounded-xl border-2 border-[#40E0D0] overflow-hidden ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <div className="bg-gradient-to-r from-[#40E0D0]/15 to-[#0066FF]/10 px-6 py-4 border-b border-[#40E0D0]/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#40E0D0] text-black tracking-wide">CURRENT PLAN</span>
                        <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Free for a limited time</span>
                      </div>
                      <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Active
                      </span>
                    </div>
                  </div>
                  <div className="px-6 py-5">
                    <p className="text-2xl font-bold mb-1">
                      <span className={theme === 'dark' ? 'text-white' : 'text-black'}>Perplexity</span><span className="text-[#40E0D0]">ATS</span>
                    </p>
                    <p className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                      Free Account — Access to Perplexity's Workday Development &amp; Consulting Services
                    </p>
                    <p className={`text-xs font-semibold mt-2 mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Available features:
                    </p>

                    <div className="space-y-3">
                      {[
                        {
                          icon: '📋',
                          label: 'Post Job Openings',
                          desc: null,
                        },
                        {
                          icon: '🔍',
                          label: 'Browse Workday Talent',
                          desc: null,
                        },
                        {
                          icon: '📄',
                          label: 'Request Interviews',
                          desc: 'Submit interview requests directly through the application. Perplexity coordinates scheduling, confirms availability, and manages all communication between your team and the consultant.',
                        },
                        {
                          icon: '⏱️',
                          label: 'Timesheet Approvals',
                          desc: 'Once a Workday contractor is placed, Perplexity provides full timesheet submission and approval management — giving you visibility and control over hours worked per engagement.',
                        },
                        {
                          icon: '🤝',
                          label: 'Perplexity Contractor Onboarding',
                          desc: 'Perplexity manages all onboarding activities for your consultant or direct-hire candidate, including messaging coordination, job reference verification, background checks, and drug screening.',
                        },
                        {
                          icon: '📁',
                          label: 'Document Management',
                          desc: 'Securely share documents and resources with your contractor — including assignment details, company policies, onboarding materials, and position-specific resources.',
                        },
                        {
                          icon: '👥',
                          label: 'Team Invitations',
                          desc: '2 seats available',
                        },
                      ].map(({ icon, label, desc }) => (
                        <div key={label} className={`flex gap-3 px-4 py-3 rounded-lg ${
                          theme === 'dark' ? 'bg-gray-700/60' : 'bg-gray-50'
                        }`}>
                          <span className="text-lg flex-shrink-0 mt-0.5">{icon}</span>
                          <div>
                            <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>{label}</p>
                            {desc && <p className={`text-xs mt-0.5 leading-relaxed ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{desc}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Subscription & Billing Card */}
                <div className={`rounded-xl border overflow-hidden ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  <div className={`px-6 py-4 border-b flex items-center justify-between ${
                    theme === 'dark' ? 'border-gray-700 bg-gray-700/40' : 'border-gray-100 bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-2">
                      <CreditCard className={`w-4 h-4 ${theme === 'dark' ? 'text-[#40E0D0]' : 'text-[#0066FF]'}`} />
                      <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Subscription &amp; Billing
                      </span>
                    </div>
                    {(() => {
                      const bs = getBillingState(billingSub);
                      const isProOrGrace = bs === 'PRO_ACTIVE' || bs === 'PRO_GRACE';
                      return (
                        <button
                          onClick={() => navigate(isProOrGrace ? '/workday-client/billing' : '/workday-client/upgrade')}
                          className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#40E0D0] to-[#0066FF] text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {isProOrGrace ? 'Manage Subscription' : 'Upgrade to Pro'}
                        </button>
                      );
                    })()}
                  </div>

                  <div className="px-6 py-5">
                    <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      Upgrade to Pro for self-service hiring, direct talent access, seat management, and job credits.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        {
                          icon: CreditCard,
                          title: 'Plan Management',
                          desc: 'Upgrade, downgrade, or cancel your Pro subscription anytime'
                        },
                        {
                          icon: Users,
                          title: 'Team Seats',
                          desc: '3 included seats on Pro — add more for $99.99 each'
                        },
                        {
                          icon: Briefcase,
                          title: 'Job Credits',
                          desc: 'Buy packs of 15, 35, or 65 job post credits via PayPal'
                        },
                        {
                          icon: Shield,
                          title: 'Grace Period Protection',
                          desc: 'Full Pro access continues until end of billing cycle after cancellation'
                        },
                      ].map(({ icon: Icon, title, desc }) => (
                        <div key={title} className={`flex gap-3 p-3.5 rounded-lg border ${
                          theme === 'dark'
                            ? 'bg-gray-700/50 border-gray-600'
                            : 'bg-gray-50 border-gray-100'
                        }`}>
                          <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${theme === 'dark' ? 'text-[#40E0D0]' : 'text-[#0066FF]'}`} />
                          <div>
                            <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{title}</p>
                            <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {(() => {
                      const bs = getBillingState(billingSub);
                      const isProOrGrace = bs === 'PRO_ACTIVE' || bs === 'PRO_GRACE';
                      return (
                        <button
                          onClick={() => navigate(isProOrGrace ? '/workday-client/billing' : '/workday-client/upgrade')}
                          className={`mt-4 w-full py-2.5 rounded-lg text-sm font-semibold transition-all border ${
                            theme === 'dark'
                              ? 'bg-[#40E0D0]/10 border-[#40E0D0]/30 text-[#40E0D0] hover:bg-[#40E0D0]/20'
                              : 'bg-[#0066FF]/5 border-[#0066FF]/20 text-[#0066FF] hover:bg-[#0066FF]/10'
                          }`}
                        >
                          {isProOrGrace ? 'View Subscription & Billing Details' : 'View Upgrade Options'}
                        </button>
                      );
                    })()}
                  </div>
                </div>

                </div>{/* end grid */}

                {/* Invoices */}
                <div>
                  <h3 className={`font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Invoices</h3>
                  {invoicesLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div>
                    </div>
                  ) : invoices.length > 0 ? (
                    <div className="space-y-3">
                      {invoices.map((invoice) => (
                        <div
                          key={invoice.id}
                          className={`p-4 rounded-lg border ${
                            theme === 'dark'
                              ? 'bg-gray-800 border-gray-700'
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-2">
                                <h4 className="font-semibold">Invoice #{invoice.invoice_number}</h4>
                                <span className={`ml-3 px-3 py-1 rounded-full text-xs font-medium ${
                                  invoice.is_paid
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {invoice.is_paid ? 'Paid' : 'Pending'}
                                </span>
                              </div>
                              <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                {invoice.description}
                              </p>
                              <div className="flex items-center space-x-4 text-sm">
                                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                  Date: {formatDate(invoice.invoice_date)}
                                </span>
                                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                  Due: {formatDate(invoice.due_date)}
                                </span>
                                {invoice.is_paid && invoice.paid_date && (
                                  <span className="text-green-600">Paid: {formatDate(invoice.paid_date)}</span>
                                )}
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-xl font-bold text-[#40E0D0]">
                                {formatCurrency(invoice.amount)}
                              </p>
                              {invoice.invoice_url && (
                                <a
                                  href={invoice.invoice_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center mt-2 text-sm text-[#40E0D0] hover:underline"
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Download
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-center py-10 rounded-lg border ${
                      theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'
                    }`}>
                      <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p className="font-medium">No Invoices Yet</p>
                      <p className="text-sm mt-1">Invoices will appear here once generated.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'delete-account' && (
              <section className={`${
                theme === 'dark'
                  ? 'bg-gray-800 border-red-900/50'
                  : 'bg-white border-red-200'
              } border rounded-xl p-6`}>
                <h2 className="text-xl font-semibold mb-2 flex items-center text-red-500">
                  <Trash2 className="w-5 h-5 mr-2" />
                  Delete Account
                </h2>
                <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Request the permanent deletion of your account and all associated data. This action cannot be undone once processed by an administrator.
                </p>

                {existingDeletionRequest ? (
                  <div className={`p-4 rounded-lg border ${
                    existingDeletionRequest.status === 'pending'
                      ? theme === 'dark' ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
                      : existingDeletionRequest.status === 'dismissed'
                      ? theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      : theme === 'dark' ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        existingDeletionRequest.status === 'pending' ? 'text-yellow-500'
                        : existingDeletionRequest.status === 'dismissed' ? 'text-gray-400'
                        : 'text-red-500'
                      }`} />
                      <div>
                        <p className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {existingDeletionRequest.status === 'pending' && 'Deletion Request Pending'}
                          {existingDeletionRequest.status === 'approved' && 'Deletion Request Approved'}
                          {existingDeletionRequest.status === 'dismissed' && 'Deletion Request Dismissed'}
                        </p>
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {existingDeletionRequest.status === 'pending' && 'Your request has been submitted and is awaiting review by an administrator.'}
                          {existingDeletionRequest.status === 'approved' && 'Your account deletion has been approved and will be processed.'}
                          {existingDeletionRequest.status === 'dismissed' && 'Your deletion request was dismissed. Contact support if you have questions.'}
                        </p>
                        <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                          Requested: {new Date(existingDeletionRequest.requested_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : showDeleteConfirm ? (
                  <div className={`p-5 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-900 border-red-800' : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start gap-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <p className={`text-sm font-medium ${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>
                        This will send a deletion request to the admin. Your account will remain active until an administrator processes the request.
                      </p>
                    </div>
                    <div className="mb-4">
                      <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Reason for deletion (optional)
                      </label>
                      <textarea
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        rows={3}
                        placeholder="Tell us why you want to delete your account..."
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none ${
                          theme === 'dark'
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                    </div>
                    {deleteMessage && (
                      <p className="text-sm text-red-500 mb-4">{deleteMessage}</p>
                    )}
                    <div className="flex gap-3">
                      <button
                        onClick={handleDeleteAccountRequest}
                        disabled={isSubmittingDelete}
                        className="flex items-center px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmittingDelete ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                        ) : (
                          <><Trash2 className="w-4 h-4 mr-2" />Submit Request</>
                        )}
                      </button>
                      <button
                        onClick={() => { setShowDeleteConfirm(false); setDeleteReason(''); setDeleteMessage(''); }}
                        className={`px-4 py-2 font-medium rounded-lg transition-colors ${
                          theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {deleteMessage && (
                      <p className={`text-sm mb-4 ${deleteMessage.includes('submitted') ? 'text-green-500' : 'text-red-500'}`}>
                        {deleteMessage}
                      </p>
                    )}
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="flex items-center px-5 py-2.5 border border-red-500 text-red-500 font-semibold rounded-lg hover:bg-red-500 hover:text-white transition-colors"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Request Account Deletion
                    </button>
                  </>
                )}
              </section>
            )}

            {activeTab === '2fa-security' && (
              <section className={`${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-200'
              } border rounded-xl p-6`}>
                <h2 className="text-xl font-semibold mb-6 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-[#40E0D0]" />
                  Two-Factor Authentication
                </h2>

                <div className="space-y-6">
                  <div className={`p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
                  }`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                      <strong>Important:</strong> Two-factor authentication (2FA) adds an extra layer of security to your account by requiring a code from your phone in addition to your password.
                    </p>
                  </div>

                  <div className={`p-6 rounded-lg border-2 ${
                    theme === 'dark' ? 'border-[#40E0D0]/30 bg-gray-900/50' : 'border-[#40E0D0] bg-gradient-to-br from-gray-50 to-white'
                  }`}>
                    <h3 className="text-lg font-semibold mb-4 flex items-center text-[#40E0D0]">
                      <Shield className="w-5 h-5 mr-2" />
                      How to Change Your Email
                    </h3>

                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-[#40E0D0] text-white rounded-full flex items-center justify-center font-bold text-sm">
                          1
                        </div>
                        <div className="flex-1">
                          <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Verify Your Current Password
                          </p>
                          <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Click "Change Email" and enter your current account password to confirm your identity before making any changes
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-[#40E0D0] text-white rounded-full flex items-center justify-center font-bold text-sm">
                          2
                        </div>
                        <div className="flex-1">
                          <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Enter Your New Email Address
                          </p>
                          <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Type the new email address you want to use for your account going forward
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-[#40E0D0] text-white rounded-full flex items-center justify-center font-bold text-sm">
                          3
                        </div>
                        <div className="flex-1">
                          <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Scan the New QR Code
                          </p>
                          <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            A new QR code will be generated tied to your new email. Open Google Authenticator, add a new account, and scan the QR code — or enter the manual entry key if you prefer
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-[#40E0D0] text-white rounded-full flex items-center justify-center font-bold text-sm">
                          4
                        </div>
                        <div className="flex-1">
                          <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Enter the Verification Code
                          </p>
                          <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Google Authenticator will display a 6-digit code for your new entry. Enter it to confirm the change and complete your email update
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className={`mt-6 p-4 rounded-lg ${
                      theme === 'dark' ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                    }`}>
                      <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                        <strong>After Changing:</strong> Your Google Authenticator will be automatically re-linked to your new email. Use your new email address to log in going forward.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
